<script setup>
import { createServiceSchema } from '~/schemas/Service';

const { data: page } = await useAsyncData(() => 
  queryCollection('SmartIvrPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(createServiceSchema({
        name: "تلفن گویای هوشمند",
        alternateName: "Smart IVR",
        type: "Interactive Voice Response System",
        description: "تلفن گویا برای شما مانند منشی خستگی‌ناپذیر کار خواهد کرد. تلفن گویای هوشمند نواتل (IVR)، کمک می‌کند تا بتوانید راحت‌تر حجم بالای تماس‌ها را مدیریت کنید. به تلفن گویای خود اجازه دهید کارهای روتین را مدیریت کند تا اپراتورهای شما بتوانند تماس‌های ورودی هدفمند بیشتری را پاسخگو باشند.",
        url: "https://navatel.ir/products/smart-ivr/",
        img: "/img/features/IVR.webp"
      }))
    },
  ],
});

const modalsStore = useModalsStore();

function handleOpenModal() {
  modalsStore.openModal('CTA');
};

function handleOnClickViewPrices() {
  const elem = document.getElementById('sound-samples');
  elem?.scrollIntoView({ behavior: 'smooth' });
};
</script>

<template>
  <template v-if="page">
    <SectionsBase class="hero-background">
      <div 
        class="absolute w-full h-1/2 bottom-0 left-0 bg-linear-to-b from-transparent to-secondary-200 
          lg:rounded-br-[140px] lg:bottom-auto lg:top-0 lg:w-[calc(50vw-125px)] lg:h-full lg:bg-linear-to-bl lg:from-secondary-200
          lg:to-transparent 2xl:w-[calc(50vw-233px)]"
      />
      <div class="container flex flex-col lg:flex-row items-center relative z-1 gap-15 lg:gap-0">
        <div class="w-full lg:mt-25">
          <h1 class="text-primary-600 highlight">{{ page.title }}</h1>
          <span class="h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
          <p class="text-gray-500 text-body1-semibold mt-6 max-w-200 text-center lg:text-start">{{ page.description }}</p>
          <div class="w-full flex flex-col lg:flex-row items-center my-10 lg:my-15 gap-5">
            <Button
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              pill
              @click="handleOpenModal"
              class="w-57"
            >
              مشاوره و راه‌اندازی رایگان
            </Button>
            <Button
              type="Outline"
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              pill
              @click="handleOnClickViewPrices"
              class="w-57 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
            >
              شنیدن نمونه‌ صداها
            </Button>
          </div>
          <div class="desktop-only w-full lg:flex! flex-col my-15 gap-3">
            <div 
              v-for="item in page.items"
              :key="item"
              class="flex items-start gap-3"
            >
              <Icon name="solar:check-circle-bold" size="24" class="text-secondary-600" />
              <span class="text-gray-400 text-body1-semibold">{{ item }}</span>
            </div>
          </div>
        </div>
        <NuxtImg
          width="520"
          height="430"
          :src="page.img"
          :alt="page.title"
          sizes="200px md:350px lg:500px 2xl:520px"
          :preload="{ fetchPriority: 'high' }"
          fetchpriority="high"
          loading="eager"
          class="mx-auto"
        />
        <div 
          class="absolute w-full max-w-100 aspect-square -bottom-12 bg-linear-to-b from-secondary-300 to-transparent 
            rounded-full md:max-w-none md:w-125 lg:bottom-auto lg:top-0 lg:left-16 -z-1"
        />
      </div>
    </SectionsBase>

    <LazySectionsFeaturesSmartIvr
      hydrate-on-visible
      :title="page.features.title"
      :desc="page.features.desc"
      :items="page.features.items"    
    />

    <LazySectionsFacilitiesSmartIvr
      hydrate-on-visible
      :title="page.facilities.title"
      :desc="page.facilities.desc"
      :items="page.facilities.items"
    />

    <LazySectionsSoundSamples hydrate-on-visible />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />

    <LazySectionsCTA 
      hydrate-on-visible 
      :title="page.CTA.title" 
      :desc="page.CTA.desc"
    />
  </template>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.hero-background {
  @apply
  relative bg-linear-to-b from-primary-100 to-transparent to-10% lg:bg-none lg:mb-10
}
.hero-background::before {
  @apply
  content-[''] absolute w-full h-full top-0 right-0 lg:bg-[url('/img/new/backgrounds/mesh-05.webp')] bg-no-repeat lg:bg-position-[100%] 
  lg:bg-size-[100%_600px] opacity-40
}
</style>